import { css } from '@emotion/css';
import usdtApi from 'apis/usdtApi';
import globalStyle from 'constants/global.style';
import { colors } from 'constants/theme';
import QRCode from 'qrcode';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { NumericFormat } from 'react-number-format';

const quickAmounts = [1000000, 2000000, 3000000, 4000000, 5000000, 6000000, 7000000, 8000000, 9000000, 10000000];
const usdtProviderCode = 'U8PAY';
const defaultUsdtNetwork = 'U8Pay_BSC';

interface UsdtAddressInfo {
    id?: string;
    accountId?: string;
    address: string;
    minDeposit?: string;
}

const parseJsonString = <T,>(value: any, fallback: T): T => {
    if (!value) return fallback;
    if (typeof value !== 'string') return value;
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
};

const pickBscNetwork = (networks: any[]) => {
    const networkNames = networks.flatMap((item) => (typeof item === 'string' ? [item] : Object.keys(item || {}).concat(Object.values(item || {}) as string[])));
    return String(networkNames.find((item) => String(item).toUpperCase().includes('BSC')) || networkNames[0] || defaultUsdtNetwork);
};

function UsdtDeposit() {
    const [amountDeposit, setAmountDeposit] = useState('0');
    const [qrImage, setQrImage] = useState('');
    const [walletInfo, setWalletInfo] = useState<UsdtAddressInfo>({ address: '' });
    const [exchangeRate, setExchangeRate] = useState(0);
    const [network, setNetwork] = useState(defaultUsdtNetwork);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        loadUsdtInfo();
    }, []);

    useEffect(() => {
        const generateQRCode = async (address: string) => {
            if (!address) {
                setQrImage('');
                return;
            }
            try {
                const qrDataUrl = await QRCode.toDataURL(address, { errorCorrectionLevel: 'high', width: 260, margin: 1 });
                setQrImage(qrDataUrl);
            } catch (error) {
                console.log(error);
            }
        };

        generateQRCode(walletInfo.address);
    }, [walletInfo.address]);

    const loadUsdtInfo = async () => {
        setLoading(true);
        try {
            const [networkResponse, addressResponse] = await Promise.all([usdtApi.getNetworks(), usdtApi.getWalletAddress({ providerCode: usdtProviderCode })]);
            const networks = parseJsonString<any[]>(networkResponse.data.usdtNetworks, []);
            const configs = parseJsonString<Record<string, string>[]>(networkResponse.data.usdtConfigs, []);
            const address = parseJsonString<UsdtAddressInfo>(addressResponse.data.address, { address: '' });
            const rateConfig = configs.find((item) => Object.prototype.hasOwnProperty.call(item, 'RATE_PAYIN'));

            setNetwork(pickBscNetwork(networks));
            setExchangeRate(Number(rateConfig?.RATE_PAYIN) || 0);
            setWalletInfo(address);

            if (addressResponse.data.msg) toast.error(addressResponse.data.msg);
        } catch (error) {
            console.log(error);
            toast.error('Không thể lấy thông tin nạp USDT');
        } finally {
            setLoading(false);
        }
    };

    const usdtAmount = useMemo(() => {
        const amount = Number(amountDeposit.replace(/[,]/g, '')) || 0;
        if (!exchangeRate) return 0;
        return amount / exchangeRate;
    }, [amountDeposit, exchangeRate]);

    const copyText = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Sao chép thành công!');
    };

    return (
        <div className={styles.wrap}>
            <h3 className={styles.title}>Chỉ hỗ trợ USDT trên mạng lưới BSC (BEP20)</h3>
            <p className={styles.networkText}>Mạng hiện tại: {network}</p>
            <p className={styles.subTitle}>Địa chỉ ví chuyển tiền vào</p>

            <div className={styles.qrWrap}>
                {qrImage ? <img className={styles.qrImg} src={qrImage} alt="USDT BEP20 QR" /> : <div className={styles.qrPlaceholder}>{loading ? 'Đang tải...' : 'Không có dữ liệu'}</div>}
            </div>

            <div className={styles.addressBox}>
                <span>{walletInfo.address || 'Đang cập nhật địa chỉ ví'}</span>
                <button type="button" className={`${globalStyle.btnCopy} btn-primary`} disabled={!walletInfo.address} onClick={() => copyText(walletInfo.address)}>
                    <img src={require('assets/images/icons/copy.png')} alt="" />
                </button>
            </div>

            <p className={styles.warning}>
                *Lưu ý: Bạn chỉ có thể chuyển token <span>USDT-BEP20</span> vào địa chỉ ví này. Mọi token khác có thể bị thất lạc trong quá trình giao dịch.
            </p>

            <div className={styles.formArea}>
                <div className={styles.inputRow}>
                    <label>Số điểm nạp</label>
                    <NumericFormat allowNegative={false} className={styles.amountInput} thousandSeparator="," value={amountDeposit} onChange={(e) => setAmountDeposit(e.target.value || '0')} />
                </div>

                <div className={styles.metaLine}>
                    <span>USDT: </span>
                    <strong>{usdtAmount ? usdtAmount.toFixed(2) : '0'}</strong>
                    <button type="button" className={`${globalStyle.btnCopy} btn-primary`} onClick={() => copyText(usdtAmount.toFixed(2))}>
                        <img src={require('assets/images/icons/copy.png')} alt="" />
                    </button>
                </div>

                <div className={styles.metaLine}>
                    <span>Tỷ giá quy đổi: 1USDT = </span>
                    <strong>{exchangeRate ? new Intl.NumberFormat().format(exchangeRate) : 'Đang cập nhật'} VND</strong>
                    <button type="button" className={styles.refreshBtn} aria-label="refresh rate" disabled={loading} onClick={loadUsdtInfo}>
                        ↻
                    </button>
                </div>

                {walletInfo.minDeposit && <p className={styles.minDeposit}>Nạp tối thiểu: {walletInfo.minDeposit} USDT</p>}

                <div className={styles.amountList}>
                    {quickAmounts.map((amount) => (
                        <button type="button" className={styles.amountItem} key={amount} onClick={() => setAmountDeposit(String(amount))}>
                            {new Intl.NumberFormat().format(amount)} VND
                        </button>
                    ))}
                </div>
            </div>

            <ul className={styles.noteList}>
                <li>Sao chép chính xác địa chỉ ví hoặc quét mã QR để thực hiện chuyển tiền.</li>
                <li>Vui lòng làm lệnh nạp mới ở mỗi lần chuyển tiền tiếp theo để cập nhật ví USDT mới nhất.</li>
            </ul>
        </div>
    );
}

const styles = {
    wrap: css`
        max-width: 620px;
        margin: 34px auto 0;
        color: #222;
        text-align: center;
    `,
    title: css`
        font-size: 18px;
        font-weight: 500;
        margin-bottom: 14px;
    `,
    subTitle: css`
        font-size: 13px;
        margin-bottom: 12px;
    `,
    networkText: css`
        color: #c9342c;
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 8px;
    `,
    qrWrap: css`
        width: 286px;
        height: 286px;
        padding: 18px;
        margin: 0 auto 26px;
        background: ${colors.white};
        box-shadow: 0 12px 22px rgba(0, 0, 0, 0.25);
    `,
    qrImg: css`
        display: block;
        width: 100%;
        height: 100%;
    `,
    qrPlaceholder: css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        color: #777;
        font-size: 14px;
    `,
    addressBox: css`
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        border-top: 1px solid #d8d8d8;
        border-bottom: 1px solid #d8d8d8;
        padding: 10px 0;
        font-size: 14px;
        font-weight: 600;
        word-break: break-all;
    `,
    warning: css`
        margin: 10px 0 24px;
        color: #c9342c;
        font-size: 14px;
        line-height: 1.45;
        span {
            font-weight: 700;
            text-decoration: underline;
        }
    `,
    formArea: css`
        width: 380px;
        max-width: 100%;
        margin: 0 auto;
        text-align: left;
    `,
    inputRow: css`
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 6px;
        label {
            flex: 0 0 92px;
            font-size: 14px;
        }
    `,
    amountInput: css`
        flex: 1;
        height: 38px;
        border: 0;
        border-radius: 3px;
        background: #c7c7c7;
        padding: 0 12px;
        font-size: 15px;
        color: #222;
        outline: none;
    `,
    metaLine: css`
        display: flex;
        align-items: center;
        gap: 5px;
        margin-left: 104px;
        min-height: 24px;
        font-size: 14px;
        strong {
            color: #d83a36;
            font-weight: 500;
        }
    `,
    refreshBtn: css`
        border: 0;
        background: transparent;
        color: #777;
        cursor: pointer;
        font-size: 20px;
        line-height: 1;
        padding: 0;
    `,
    minDeposit: css`
        margin: 4px 0 0 104px;
        color: #c9342c;
        font-size: 13px;
    `,
    amountList: css`
        display: flex;
        flex-wrap: wrap;
        gap: 9px;
        margin-top: 14px;
    `,
    amountItem: css`
        min-width: 118px;
        height: 32px;
        border: 0;
        background: #c8c8c8;
        color: ${colors.white};
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        &:hover {
            background: #b8b8b8;
        }
    `,
    noteList: css`
        max-width: 620px;
        margin: 34px auto 0;
        padding-left: 26px;
        text-align: left;
        font-size: 14px;
        line-height: 1.45;
        li {
            margin-bottom: 8px;
        }
        li::marker {
            color: red;
            font-size: 18px;
        }
    `,
};

export default UsdtDeposit;
